// CompanyDetailsDialog.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Grid, LinearProgress, Paper, Skeleton, Stack, Tooltip, Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
    Business as BusinessIcon,
    OpenInNew as OpenInNewIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { useAppDispatch } from "../../app/hooks";
import { loadCompanyDetails, MarketplaceCompanyDetails, MarketplaceDevice } from "./marketPlaceApi";
import { MARKET_REGION_LABEL, MarketRegionEnum } from "../administration/companyProfile/marketRegion";
import { CERT_COLOR, certCategory, CERTIFICATION_LABEL, CertificationStandardEnum } from "../administration/companyProfile/companyCertificates";
import { StructureName } from "../device/Structure";
import { Group as GroupIcon, CalendarMonth as CalendarMonthIcon } from "@mui/icons-material";
import {
    Public as PublicIcon,
    Phone as PhoneIcon,
    LocationOn as LocationOnIcon,
    LinkedIn as LinkedInIcon,
    Twitter as TwitterIcon,
    Facebook as FacebookIcon,
    Email as EmailIcon,
} from "@mui/icons-material";
import { CloseButton } from "../../app/components/CloseButton";
import { QUICK_TAG_LABEL, QuickTagEnum } from "../administration/companyProfile/enums/QuickTagEnum";

const COMPANY_SIZE_TIER_NUM_LABEL: Record<number, string> = {
    1: '1–9',
    2: '10–49',
    3: '50–99',
    4: '100–249',
    5: '250–499',
    6: '500–999',
    7: '1000–4999',
    8: '5000–9999',
    9: '10000–24999',
    10: '25000–49999',
    11: '50000+',
};

const normalizeUrl = (url?: string | null) => {
    if (!url) return "";
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
};

export function CompanyDetailsDialog({
    companyId,
    open,
    onClose,
}: {
    companyId: number | null;
    open: boolean;
    onClose: () => void;
}) {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const [data, setData] = useState<MarketplaceCompanyDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [initialLoaded, setInitialLoaded] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);

    // Ładuj dane przy otwarciu i zmianie companyId
    useEffect(() => {
        if (!open || companyId == null) return;
        setLoading(true);
        dispatch(loadCompanyDetails({ companyId }))
            .unwrap()
            .then((res) => { setData(res); setError(null); })
            .catch((e) => setError(e))
            .finally(() => { setLoading(false); setInitialLoaded(true); });
    }, [open, companyId, dispatch]);

    // Labelki rynków z kodów
    const marketLabels = useMemo(() => {
        const codes = data?.markets ?? [];
        return codes
            .map((code) => MARKET_REGION_LABEL[code as MarketRegionEnum])
            .filter((x): x is string => Boolean(x));
    }, [data]);

    const isSkeleton = loading && !initialLoaded;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            scroll="paper"
            PaperProps={{
                sx: {
                    height: { xs: "100%", md: "90vh" },
                    borderRadius: 2,
                }
            }}
        >
            {loading && (
                <Box sx={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <LinearProgress />
                </Box>
            )}

            <DialogTitle sx={{ pb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    {data?.logoUrl ? (
                        <Avatar
                            src={data?.logoUrl}
                            variant="rounded"
                            sx={{
                                width: 56,
                                height: 56,
                                border: "0px solid",
                                borderColor: "divider",
                                bgcolor: "background.default",
                                "& .MuiAvatar-img": {
                                    objectFit: "contain",
                                    padding: "2px",
                                },
                            }}
                        />
                    ) : (
                        <Avatar sx={{ width: 56, height: 56, border: "1px solid", borderColor: "divider" }}>
                            <BusinessIcon />
                        </Avatar>
                    )}

                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" noWrap title={data?.name}>{data?.name ?? "—"}</Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}
                        >
                            {data?.countryCode ? `${data.countryCode} • ` : ""}
                            {[data?.country, data?.city].filter(Boolean).join(" / ")}
                        </Typography>

                    </Box>
                    <Box sx={{ flexGrow: 1 }} />

                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.02) }}>
                {/* About (2 kolumny: About + meta) */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Lewa kolumna: About */}
                    <Grid item xs={12} md={8}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                height: "100%",
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: (t) => t.shadows[2],
                                transition: "box-shadow .2s, transform .02s",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {/* NAGŁÓWEK: About + nazwa firmy na jasnym tle */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1.25,
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    bgcolor: (t) => t.palette.action.hover,
                                }}
                            >
                                <Typography variant="h6" sx={{ mr: 0.5 }}>
                                    About
                                </Typography>

                                <Tooltip title={data?.name ?? ""} disableInteractive>
                                    <Typography
                                        variant="h6"
                                        fontWeight={800}
                                        sx={{
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            flex: 1,
                                        }}
                                    >
                                        {data?.name ?? "—"}
                                    </Typography>
                                </Tooltip>
                            </Box>

                            {/* Treść sekcji */}
                            {isSkeleton ? (
                                <Stack spacing={1}>
                                    <Skeleton width="85%" />
                                    <Skeleton width="92%" />
                                    <Skeleton width="70%" />
                                </Stack>
                            ) : (
                                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                                    {data?.description || "—"}
                                </Typography>
                            )}

                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                    mt: "auto",
                                }}
                            ><Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                    {(data?.quickTags ?? []).map((tag) => {
                                        const label = QUICK_TAG_LABEL[tag as QuickTagEnum];
                                        if (!label) return null;
                                        return <Chip key={tag} size="small" label={label} />;
                                    })}
                                </Stack>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Prawa kolumna: logo + size + founded */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3}
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                height: "100%",
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: (t) => t.shadows[2],
                                transition: "box-shadow .2s, transform .02s",
                            }}>
                            {isSkeleton ? (
                                <Stack spacing={1.5} alignItems="center">
                                    <Skeleton variant="circular" width={72} height={72} />
                                    <Grid container spacing={1} sx={{ width: "100%" }}>
                                        <Grid item xs={6}><Skeleton width="80%" /></Grid>
                                        <Grid item xs={6}><Skeleton width="80%" /></Grid>
                                    </Grid>
                                </Stack>
                            ) : (
                                <Stack spacing={1.5} alignItems="center">
                                    {data?.logoUrl ? (
                                        <Box
                                            component="img"
                                            src={data.logoUrl}
                                            alt={data?.name ? `${data.name} logo` : "Company logo"}
                                            loading="lazy"
                                            referrerPolicy="no-referrer"
                                            sx={{
                                                width: "100%",          // dopasuj do szerokości kontenera
                                                maxHeight: 150,         // ogranicz wysokość (usuń jeśli chcesz pełny rozmiar)
                                                objectFit: "contain",   // NIC nie przycina, zachowuje proporcje
                                                display: "block",
                                                borderColor: "divider",
                                                borderRadius: 2,
                                                bgcolor: "background.default",
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            sx={{
                                                width: 72,
                                                height: 72,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                bgcolor: "background.default",
                                            }}
                                        >
                                            <BusinessIcon />
                                        </Avatar>
                                    )}

                                    <Tooltip title={data?.name ?? ""} disableInteractive>
                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                            sx={{
                                                textAlign: "center",
                                                maxWidth: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                            }}
                                        >
                                            {data?.name ?? "—"}
                                        </Typography>
                                    </Tooltip>
                                    <Divider flexItem />

                                    <Grid container spacing={1.5} sx={{ width: "100%" }}>
                                        <Grid item xs={6}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                                                <GroupIcon fontSize="medium" sx={{ color: "success.main" }} />
                                                <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
                                                    Company size
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    display: "inline-block",
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    bgcolor: (t) => t.palette.action.hover,
                                                }}
                                            >
                                                {data?.companySizeTierId != null
                                                    ? (COMPANY_SIZE_TIER_NUM_LABEL[data.companySizeTierId] ?? `Tier ${data.companySizeTierId}`)
                                                    : "—"}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                                                <CalendarMonthIcon fontSize="medium" sx={{ color: "info.main" }} />
                                                <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
                                                    Founded
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    display: "inline-block",
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    bgcolor: (t) => t.palette.action.hover,
                                                }}
                                            >
                                                {data?.yearFounded ?? "—"}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                </Stack>
                            )}
                        </Paper>
                    </Grid>
                </Grid>


                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* LEWA kolumna: Markets + Certificates */}
                    <Grid item xs={12} md={8}>
                        {/* Markets */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1.25,
                                    px: 1.25,
                                    py: 0.25,
                                    borderRadius: 1.5,
                                    bgcolor: (t) => t.palette.action.hover,
                                }}
                            >
                                <Typography variant="h6">Markets</Typography>
                            </Box>
                            {isSkeleton ? (
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <Skeleton key={i} variant="rounded" width={120} height={28} />
                                    ))}
                                </Stack>
                            ) : marketLabels.length ? (
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                                    {marketLabels.map((label) => (
                                        <Chip key={label} size="small" label={label} />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography color="text.secondary">—</Typography>
                            )}
                        </Paper>

                        {/* Certificates */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1.25,
                                    px: 1.25,
                                    py: 0.25,
                                    borderRadius: 1.5,
                                    bgcolor: (t) => t.palette.action.hover,
                                }}
                            >
                                <Typography variant="h6" >Certificates</Typography>
                            </Box>
                            {isSkeleton ? (
                                <Stack spacing={1}>
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <Skeleton key={i} height={24} />
                                    ))}
                                </Stack>
                            ) : (data?.certificates?.length ?? 0) > 0 ? (
                                (() => {
                                    const raw = (data!.certificates ?? []) as number[];
                                    const codes = Array.from(
                                        new Set(raw.filter(c => !!CERTIFICATION_LABEL[c as CertificationStandardEnum]))
                                    );
                                    const order = (cat: string) => ["ISO", "GxP", "Conformity"].indexOf(cat);
                                    codes.sort((a, b) => {
                                        const ca = certCategory(a), cb = certCategory(b);
                                        if (ca !== cb) return order(ca) - order(cb);
                                        const la = CERTIFICATION_LABEL[a as CertificationStandardEnum]!;
                                        const lb = CERTIFICATION_LABEL[b as CertificationStandardEnum]!;
                                        return la.localeCompare(lb, "en", { sensitivity: "base" });
                                    });

                                    return (
                                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                                            {codes.map(code => {
                                                const label = CERTIFICATION_LABEL[code as CertificationStandardEnum]!;
                                                const color = CERT_COLOR[code as CertificationStandardEnum] ?? "default";
                                                return (
                                                    <Chip
                                                        key={code}
                                                        size="small"
                                                        label={label}
                                                        color={color}
                                                        variant={color === "default" ? "outlined" : "filled"}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    );
                                })()
                            ) : (
                                <Typography color="text.secondary">—</Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* PRAWA kolumna: Contact */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                height: "100%",
                                borderColor: "divider",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1.25,
                                    px: 1.25,
                                    py: 0.25,
                                    borderRadius: 1.5,
                                    bgcolor: (t) => t.palette.action.hover,
                                }}
                            >
                                <Typography variant="h6" >Contact</Typography>
                            </Box>
                            {isSkeleton ? (
                                <Stack spacing={1}>
                                    <Skeleton width="70%" />
                                    <Skeleton width="60%" />
                                    <Skeleton width="50%" />
                                </Stack>
                            ) : (
                                <Stack spacing={1.25}>
                                    {/* Adres złożony ze street + buildingNumber + unitNumber oraz postalCode + city + country */}
                                    {(data?.street || data?.buildingNumber || data?.unitNumber || data?.postalCode || data?.city || data?.country) ? (
                                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                            <LocationOnIcon fontSize="small" sx={{ mt: "2px", color: "text.secondary" }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                                    {[
                                                        [data?.street, data?.buildingNumber].filter(Boolean).join(" "),
                                                        data?.unitNumber ? `/${data.unitNumber}` : null,
                                                    ].filter(Boolean).join("") || "—"}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                    {[
                                                        [data?.postalCode, data?.city].filter(Boolean).join(" "),
                                                        data?.country ? `${data.country}` : null,
                                                    ].filter(Boolean).join(", ")}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    ) : null}

                                    {/* Telefon */}
                                    {data?.phone ? (
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                            <PhoneIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                            <Button
                                                size="small"
                                                href={`tel:${data.phone}`}
                                                sx={{ textTransform: "none", px: 0 }}
                                            >
                                                {data.phone}
                                            </Button>
                                        </Stack>
                                    ) : null}

                                    {/* Email */}
                                    {data?.email ? (
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                            <EmailIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                            <Button
                                                size="small"
                                                href={`mailto:${data.email}`}
                                                sx={{ textTransform: "none", px: 0 }}
                                            >
                                                {data.email}
                                            </Button>
                                        </Stack>
                                    ) : null}

                                    {/* WWW */}
                                    {data?.webSiteUrl ? (
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                            <PublicIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                            <Button
                                                href={normalizeUrl(data.webSiteUrl)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                endIcon={<OpenInNewIcon />}
                                                size="small"
                                                sx={{ textTransform: "none", px: 0 }}
                                            >
                                                {data.webSiteUrl.replace(/^https?:\/\//i, "")}
                                            </Button>
                                        </Stack>
                                    ) : null}
                                </Stack>
                            )}
                        </Paper>

                    </Grid>
                </Grid>




                {/* Devices */}
                <Paper variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: (t) => t.shadows[2],
                        transition: "box-shadow .2s, transform .02s",
                    }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1.25,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1.5,
                            bgcolor: (t) => t.palette.action.hover,
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1 }}>Devices</Typography>
                    </Box>
                    {isSkeleton ? (
                        <Stack spacing={1}>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Skeleton variant="rounded" width={36} height={36} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton width="40%" />
                                        <Skeleton width="70%" />
                                    </Box>
                                    <Skeleton variant="rounded" width={80} height={28} />
                                </Box>
                            ))}
                        </Stack>
                    ) : (data?.devices?.length ?? 0) > 0 ? (
                        <Stack spacing={1.25}>
                            {data!.devices!.map((d: MarketplaceDevice) => (
                                <Box
                                    key={d.id}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        p: 1.25,
                                        transition: "background-color .2s, box-shadow .2s",
                                        "&:hover": { bgcolor: (t) => t.palette.action.hover, boxShadow: (t) => t.shadows[1] },
                                    }}
                                >
                                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                        <Avatar
                                            slotProps={{
                                                img: { loading: 'lazy' }
                                            }}
                                            variant="rounded"
                                            src={d.url ?? undefined}
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                bgcolor: "background.default",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {d.name?.slice(0, 2).toUpperCase()}
                                        </Avatar>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25, minWidth: 0 }}>
                                                <Typography fontWeight={600} noWrap title={d.name}>
                                                    {d.name}
                                                </Typography>
                                                {d.structureId && (
                                                    <Chip size="small"
                                                        variant="outlined"
                                                        label={<StructureName structureId={d.structureId} />} />

                                                )}
                                            </Stack>

                                            {d.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.25, whiteSpace: "pre-wrap" }}
                                                >
                                                    <div dangerouslySetInnerHTML={{ __html: d.description }}></div>
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Typography color="text.secondary">—</Typography>
                    )}
                </Paper>

            </DialogContent>

            <DialogActions sx={{ p: 1.5 }}>
                <CloseButton loading={false} onClick={onClose}></CloseButton>
            </DialogActions>
        </Dialog>
    );
}
